import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import useConfirmDialog from '@/components/ConfirmDialogProvider';
import Container from '../../components/Container';
import { Credit, M24MediaModel } from '../../declarations/models/M24MediaModel';
import Styles from '../../assets/js/Styles';
import { Api } from '../../services/Api';
import { MediaLicense } from '../../declarations/models/MediaLicense';
import { Tag } from '../../declarations/models/Tag';
import EditMediaLicenseCreditsFields from '../../editor/MediaEditor/MediaEditorFields/EditMediaLicenseCreditsFields';
import EditMediaTagsField from '../../editor/MediaEditor/MediaEditorFields/EditMediaTagsField';
import EditMediaTextFields from '../../editor/MediaEditor/MediaEditorFields/EditMediaTextFields';

export interface EditSelectedMediaProps {
  selectedItems: Array<M24MediaModel>;
  refreshPage: () => void;
  tags: Array<Tag>;
  siteId: number;
}

export const EditSelectedMedia: FC<EditSelectedMediaProps> = ({ selectedItems, refreshPage, tags, siteId }) => {
  const { t: tComp } = useTranslation('components');
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirmDialog();

  const [license, setLicense] = useState<MediaLicense>(MediaLicense.NONE);
  const [credits, setCredits] = useState<Array<Credit>>([]);
  const [tagsInput, setTagsInput] = useState<Array<string>>([]);

  const singleSelectedItem = selectedItems.length === 1 ? selectedItems[0] : undefined;
  const [singleItemDraft, setSingleItemDraft] = useState<M24MediaModel | undefined>(singleSelectedItem);

  // When selection changes, reset the draft to the selected item
  useEffect(() => {
    setSingleItemDraft(singleSelectedItem);
  }, [singleSelectedItem?.id]);

  const hasTextChanges = useMemo(() => {
    if (!singleSelectedItem || !singleItemDraft) return false;

    const original = singleSelectedItem;
    const draft = singleItemDraft;

    const originalTitle = (original as any).title ?? original.content?.title ?? '';
    const draftTitle = (draft as any).title ?? draft.content?.title ?? '';

    return (
      originalTitle !== draftTitle ||
      (original.content?.caption ?? '') !== (draft.content?.caption ?? '') ||
      (original.content?.alttext ?? '') !== (draft.content?.alttext ?? '') ||
      JSON.stringify(original.content ?? {}) !== JSON.stringify(draft.content ?? {})
    );
  }, [singleSelectedItem, singleItemDraft]);

  const setTitle = (title: string, locale?: string) => {
    setSingleItemDraft((prev) => {
      if (!prev) return prev;
      const next: M24MediaModel = { ...prev, content: { ...(prev.content ?? {}) } };

      if (locale) {
        const prevLocale = (next.content as any)[locale] ?? {};
        (next.content as any)[locale] = { ...prevLocale, title };
      } else {
        // Some models use top-level `title`, some use `content.title` — keep them in sync.
        (next as any).title = title;
        (next.content as any).title = title;
      }

      return next;
    });
  };

  const setCaption = (caption: string, locale?: string) => {
    setSingleItemDraft((prev) => {
      if (!prev) return prev;
      const next: M24MediaModel = { ...prev, content: { ...(prev.content ?? {}) } };

      if (locale) {
        const prevLocale = (next.content as any)[locale] ?? {};
        (next.content as any)[locale] = { ...prevLocale, caption };
      } else {
        (next.content as any).caption = caption;
      }

      return next;
    });
  };

  const setAltText = (alttext: string, locale?: string) => {
    setSingleItemDraft((prev) => {
      if (!prev) return prev;
      const next: M24MediaModel = { ...prev, content: { ...(prev.content ?? {}) } };

      if (locale) {
        const prevLocale = (next.content as any)[locale] ?? {};
        (next.content as any)[locale] = { ...prevLocale, alttext };
      } else {
        (next.content as any).alttext = alttext;
      }

      return next;
    });
  };

  const resetInputs = () => {
    setLicense(MediaLicense.NONE);
    setCredits([]);
    setTagsInput([]);
    setSingleItemDraft(singleSelectedItem);
  };

  const doBatchUpdate = async () => {
    if (await confirm(tComp('EditSelectedMedia.ConfirmUpdateSelection'))) {
      const partRes: Partial<M24MediaModel> = { content: {} };

      if (credits.length) partRes.content!.credits = credits;
      if (license) partRes.content!.license_code = license;
      if (tagsInput.length) partRes.tags = tagsInput.map((tag) => ({ tag }));

      // If exactly one item is selected, allow editing text fields as well
      if (singleSelectedItem && singleItemDraft && hasTextChanges) {
        const originalContent = singleSelectedItem.content ?? {};
        const draftContent = singleItemDraft.content ?? {};

        // Default language fields
        if ((draftContent as any).title !== (originalContent as any).title)
          (partRes.content as any).title = (draftContent as any).title;
        if ((draftContent as any).caption !== (originalContent as any).caption)
          (partRes.content as any).caption = (draftContent as any).caption;
        if ((draftContent as any).alttext !== (originalContent as any).alttext)
          (partRes.content as any).alttext = (draftContent as any).alttext;

        // Locale-specific fields (content[locale])
        Object.keys(draftContent).forEach((k) => {
          const v = (draftContent as any)[k];
          if (!v || typeof v !== 'object') return;
          const originalV = (originalContent as any)[k];
          if (JSON.stringify(v) !== JSON.stringify(originalV)) {
            (partRes.content as any)[k] = v;
          }
        });
      }

      const ctx = Api.updateM24MediaResources(siteId, selectedItems.map((i) => i.id).join(','), partRes);
      const [res, err] = await ctx.fetch();

      if (res) {
        const nrFailed = res.total_count - res.res_count;
        if (nrFailed === 0) {
          enqueueSnackbar(tComp('MediaView.MediaUpdated', { nr: res.res_count }), { variant: 'success' });
        } else {
          enqueueSnackbar(tComp('MediaView.UpdatesFailed', { failed: nrFailed, total: res.total_count }), {
            variant: 'error',
          });
        }
        refreshPage();
        resetInputs();
      }

      if (err) enqueueSnackbar(err.message, { variant: 'error' });
      ctx.abort();
    }
  };

  return (
    <Container
      p={3}
      gap={3}
      sx={{ width: '500px', minWidth: '500px', backgroundColor: Styles.Colors.FINDER_M24_PRIMARY_HEADER_BACKGROUND }}
      column>
      <Typography variant='h5'>{tComp('MediaView.EditSelectedMedia')}</Typography>

      <EditMediaLicenseCreditsFields
        credits={credits}
        setCredits={setCredits}
        license={license}
        setLicense={setLicense}
      />

      <EditMediaTagsField allTags={tags} tagsInput={tagsInput} setTagsInput={setTagsInput} />

      {singleSelectedItem && singleItemDraft && (
        <EditMediaTextFields
          item={singleItemDraft}
          setTitle={setTitle}
          setCaption={setCaption}
          setAltText={setAltText}
        />
      )}

      <Container fullWidth>
        <Button
          disabled={!credits.length && !license && !tagsInput.length && !hasTextChanges}
          fullWidth
          color='success'
          onClick={doBatchUpdate}
          variant='contained'>
          {tComp('MediaView.Update_selected')}
        </Button>
      </Container>
    </Container>
  );
};

export default EditSelectedMedia;
